import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Mail, ShieldCheck, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";

const CATEGORIES = ["Produits", "Commande", "Livraison", "Réclamation", "Autre"];

export default function Contact() {
  const [step, setStep] = useState(1); // 1: form, 2: token, 3: done
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "",
    category: "Produits", message: "",
  });
  const [token, setToken] = useState("");

  const set = (k, v) => setForm({ ...form, [k]: v });

  const requestToken = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact/request-token", { email: form.email });
      toast.success("Code envoyé par email.");
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const submitMessage = async (e) => {
    e.preventDefault();
    if (token.length !== 6) { toast.error("Code à 6 chiffres requis"); return; }
    setLoading(true);
    try {
      await api.post("/contact/submit", { ...form, token });
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 py-16" data-testid="contact-page">
      <SEO pageSlug="contact" title="Contact" description="Contactez votre distributeur Herbalife indépendant pour toute question produit, commande ou livraison." path="/contact" />
      <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#7AC143] mb-3">Contact</div>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">Une question ?</h1>
      <p className="text-[#4B634B] mt-3 max-w-xl">Notre équipe vous répond rapidement. Le formulaire est réservé aux clients enregistrés (avec vérification email anti-bot).</p>

      <div className="bg-white border border-[#E5EAE5] rounded-3xl p-8 mt-10">
        {step === 1 && (
          <form onSubmit={requestToken} className="space-y-4" data-testid="contact-step1">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label htmlFor="fn">Prénom</Label><Input id="fn" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} required data-testid="contact-firstname" /></div>
              <div><Label htmlFor="ln">Nom</Label><Input id="ln" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} required data-testid="contact-lastname" /></div>
            </div>
            <div><Label htmlFor="em">Email (compte client)</Label><Input id="em" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="contact-email" /></div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="contact-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label htmlFor="msg">Message</Label><Textarea id="msg" rows={6} value={form.message} onChange={(e) => set("message", e.target.value)} required data-testid="contact-message" /></div>
            <Button type="submit" disabled={loading} data-testid="contact-request-token" className="w-full bg-[#7AC143] hover:bg-[#67BC2A] text-white rounded-full py-6 font-bold">
              <Mail className="w-4 h-4 mr-2" /> {loading ? "Envoi..." : "Recevoir un code de vérification"}
            </Button>
            <p className="text-xs text-[#4B634B] flex items-center gap-2 justify-center">
              <ShieldCheck className="w-3.5 h-3.5" /> Vérification email anti-bot · Code valide 5 minutes
            </p>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submitMessage} className="space-y-4" data-testid="contact-step2">
            <button type="button" onClick={() => setStep(1)} className="inline-flex items-center gap-2 text-sm text-[#4B634B] hover:text-[#7AC143] mb-2">
              <ArrowLeft className="w-4 h-4" /> Modifier
            </button>
            <div className="bg-[#F2F5F0] border border-[#E5EAE5] rounded-2xl p-5 text-center">
              <Mail className="w-10 h-10 text-[#7AC143] mx-auto mb-3" />
              <p className="font-semibold text-[#1A2E1A]">Code envoyé à <span className="text-[#7AC143]">{form.email}</span></p>
              <p className="text-sm text-[#4B634B] mt-1">Saisissez le code à 6 chiffres reçu par email.</p>
            </div>
            <div>
              <Label htmlFor="tk">Code de vérification</Label>
              <Input id="tk" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                value={token} onChange={(e) => setToken(e.target.value.replace(/\D/g, ""))}
                placeholder="123456" required data-testid="contact-token-input"
                className="text-center text-2xl tracking-[0.5em] font-mono" />
            </div>
            <Button type="submit" disabled={loading || token.length !== 6} data-testid="contact-submit-final" className="w-full bg-[#7AC143] hover:bg-[#67BC2A] text-white rounded-full py-6 font-bold">
              {loading ? "Envoi..." : "Valider et envoyer le message"}
            </Button>
            <p className="text-xs text-[#4B634B] text-center">Code expirable dans 5 minutes</p>
          </form>
        )}

        {step === 3 && (
          <div className="text-center py-8" data-testid="contact-step3">
            <CheckCircle2 className="w-20 h-20 text-[#7AC143] mx-auto mb-4" />
            <h2 className="text-2xl font-black tracking-tight">Message envoyé</h2>
            <p className="text-[#4B634B] mt-2">Nous vous répondrons rapidement à <strong>{form.email}</strong>.</p>
            <Link to="/" className="inline-block mt-6 bg-[#7AC143] hover:bg-[#67BC2A] text-white rounded-full px-7 py-3 font-semibold">
              Retour à l'accueil
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
